/**
 * Tenant service for TalkFlow widget.
 *
 * This service handles tenant detection and configuration.
 */

/**
 * Safely get the script tag with talkflow in the src
 *
 * @returns {HTMLElement|null} The script element or null
 */
const getTalkflowScriptTag = () => {
  try {
    // Try to get currentScript first
    if (document.currentScript) {
      return document.currentScript;
    }

    // Fallback to querySelector
    const scriptTags = document.querySelectorAll('script[src*="talkflow"]');
    if (scriptTags && scriptTags.length > 0) {
      return scriptTags[0];
    }

    return null;
  } catch (error) {
    console.error('Error getting TalkFlow script tag:', error);
    return null;
  }
};

/**
 * Detect tenant ID from various sources.
 *
 * Attempts to detect tenant ID from:
 * 1. Script data attribute
 * 2. URL parameter
 * 3. Subdomain
 * 4. Global window variable
 *
 * @returns {string} Detected tenant ID or 'default'
 */
export const detectTenant = () => {
  try {
    // Method 1: From script data attribute
    const scriptTag = getTalkflowScriptTag();
    if (scriptTag && scriptTag.dataset && scriptTag.dataset.tenant) {
      return scriptTag.dataset.tenant;
    }

    // Method 2: From URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('tenant')) {
      return urlParams.get('tenant');
    }

    // Method 3: From subdomain (chat.acme-corp.com)
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length >= 3 && hostParts[0] === 'chat') {
      return hostParts[1];
    }

    // Method 4: From global window variable
    if (window.TALKFLOW_TENANT) {
      return window.TALKFLOW_TENANT;
    }
  } catch (error) {
    console.error('Error detecting tenant:', error);
  }

  // Fallback to default tenant
  return 'default';
};

/**
 * Get theme from script data attribute.
 *
 * @returns {string} Theme name or 'default'
 */
export const detectTheme = () => {
  try {
    const scriptTag = getTalkflowScriptTag();
    if (scriptTag && scriptTag.dataset && scriptTag.dataset.theme) {
      return scriptTag.dataset.theme;
    }
  } catch (error) {
    console.error('Error detecting theme:', error);
  }

  return 'default';
};

/**
 * Get widget position from script data attribute.
 *
 * @returns {string} Position ('right' or 'left')
 */
export const detectPosition = () => {
  try {
    const scriptTag = getTalkflowScriptTag();
    if (scriptTag && scriptTag.dataset && scriptTag.dataset.position) {
      return scriptTag.dataset.position;
    }
  } catch (error) {
    console.error('Error detecting position:', error);
  }

  return 'right';
};

import { getApiBaseUrl } from '../config/api.js';

/**
 * Get API URL from script data attribute or environment
 *
 * @returns {string} API URL
 */
export const detectApiUrl = () => {
  try {
    // First try script tag
    const scriptTag = getTalkflowScriptTag();
    if (scriptTag && scriptTag.dataset && scriptTag.dataset.apiUrl) {
      return scriptTag.dataset.apiUrl;
    }

    // Next try window variable
    if (window.TALKFLOW_API_URL) {
      return window.TALKFLOW_API_URL;
    }

    // Next check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('apiUrl')) {
      return urlParams.get('apiUrl');
    }
  } catch (error) {
    console.error('Error detecting API URL:', error);
  }

  // Use configured API URL as fallback
  return getApiBaseUrl();
};

/**
 * Get all widget configuration from script data attributes.
 *
 * @returns {Object} Widget configuration
 */
export const getWidgetConfig = () => {
  const scriptTag = getTalkflowScriptTag();
  const config = {
    tenant: detectTenant(),
    theme: detectTheme(),
    position: detectPosition(),
    apiUrl: detectApiUrl(),
    chatbotName: 'Trung Tâm',
    welcomeMessage: 'Kính chào quý khách! Tôi là trợ lý AI, rất hân hạnh được hỗ trợ quý khách. Quý khách cần tôi giúp đỡ điều gì ạ?',
    placeholder: 'Nhập câu hỏi của bạn...',
    buttonText: 'Gửi',
    initialSuggestions: [
      'Trung tâm tiếp nhận những đối tượng nào?',
      'Hồ sơ tiếp nhận đối tượng hưởng ngân sách gồm những gì?',
      'Đối tượng được về thăm gia đình bao nhiêu lần'
    ]
  };

  // Override config with data attributes from script tag
  if (scriptTag && scriptTag.dataset) {
    Object.keys(scriptTag.dataset).forEach(key => {
      config[key] = scriptTag.dataset[key];
    });
  }

  return config;
};

/**
 * Save widget configuration to localStorage
 *
 * @param {Object} config - Configuration to save
 */
export const saveWidgetConfig = (config) => {
  try {
    localStorage.setItem('talkflow_config', JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('Error saving widget config:', error);
    return false;
  }
};

/**
 * Load widget configuration from localStorage
 *
 * @returns {Object|null} Saved configuration or null
 */
export const loadWidgetConfig = () => {
  try {
    const savedConfig = localStorage.getItem('talkflow_config');
    return savedConfig ? JSON.parse(savedConfig) : null;
  } catch (error) {
    console.error('Error loading widget config:', error);
    return null;
  }
};
